'use strict'

import Color from '../models/Color'
import Config from '../Config.js'

/**
 * MaskColorSampleImporter
 *
 * Responsibilities:
 * - Validate color sample entries from multiple legacy payload shapes.
 * - Create Color models for valid entries.
 * - Populate ColorSample slots and draw texture previews when needed.
 *
 * Used by: database project import, uploader import, and prepaint rendering flows.
 */
class MaskColorSampleImporter {
    /**
     * @param {Object} options
     * @param {CanvasState} options.canvasState
     * @param {number|string} [options.currentSwatchId]
     * @param {Array<number|string>} [options.listOfColorChartsServerID]
     */
    constructor({ canvasState, currentSwatchId, listOfColorChartsServerID }) {
        this.canvasState = canvasState
        this.colorSample = canvasState.colorSample
        this.currentSwatchId = currentSwatchId
        this.listOfColorChartsServerID = listOfColorChartsServerID
    }

    /**
     * Create dropcolors from the provided color sample entries.
     * Returns a shift map used to remap mask indices.
     *
     * @param {Array<Object>} [colorArray=[]]
     * @returns {Promise<Map<number, number>>}
     */
    async importColorSample(colorArray = []) {
        const shiftCounter = new Map()

        if (!Array.isArray(colorArray)) return shiftCounter

        const tasks = []
        let slot = 1

        colorArray.forEach((entry, index) => {
            const maskIndex = index + 1
            shiftCounter.set(maskIndex, Config.DEFAULT_MASKING_COLOR)

            if (!this._isValidEntry(entry)) return

            const targetSlot = slot
            shiftCounter.set(maskIndex, targetSlot)

            tasks.push(this._createAndRegisterColor(entry, targetSlot))
            slot += 1
        })

        await Promise.all(tasks)
        return shiftCounter
    }

    /**
     * Decide if a color sample entry should be imported.
     * @param {Object} entry
     * @returns {boolean}
     */
    _isValidEntry(entry) {
        if (!entry) return false
        if (Array.isArray(entry)) return entry.length > 0

        if ('swatch_color' in entry && !('swatchColorInfos' in entry)) return true
        if ('swatchColorInfos' in entry && entry.swatchColorInfos.length !== 0) return true
        if ('attributes' in entry) return true
        if ('texture' in entry && entry.texture && entry.texture.length !== 0) return true

        return false
    }

    /**
     * Create and register a Color model for a color sample entry.
     * @param {Object} entry
     * @param {number} slot
     * @returns {Promise<void>}
     */
    async _createAndRegisterColor(entry, slot) {
        const result = await Color.create({
            swatch_color_data: entry,
            dropcolor_position: slot,
            current_swatch_id: this.currentSwatchId,
            listOfColorChartsServerID: this.listOfColorChartsServerID,
        })

        this.canvasState._notifyDropcolorCreated(result)
        this.colorSample.setColorByNumber(result.dropcolor_id, result)

        await this._applyTexturePreview(result)
    }

    /**
     * Draw the texture preview for texture-based colors.
     * @param {Color} color
     * @returns {Promise<void>}
     */
    _applyTexturePreview(color) {
        if (color.type !== Color.TYPE.TEXTURE && color.type !== Color.TYPE.TEXTURE_SWATCH) {
            return Promise.resolve()
        }

        return new Promise((resolve) => {
            const img = new Image()
            img.onload = () => {
                this.canvasState.renderTextureCanvas(color, color.dropcolor_id, img)
                resolve()
            }
            img.onerror = () => resolve()
            img.src = color.texture_data_base64
        })
    }
}

export { MaskColorSampleImporter }
